import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { hasIn } from 'lodash';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import {
  CapacityInfo,
  ColorsConst,
  DefaultConst,
  EligibilityService,
  Line,
  PlanSubscription,
  ResourceCapacity,
  Speed,
  SubscriptionStatus,
  TrafficLabelId,
  TrafficSpeed,
  UpdateUserPlanParams,
  UserPlanStatus,
  UserPlanV2,
} from 'src/app/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { UtilsPipe } from 'src/app/shared/pipes/utils.pipe';
import { MobileEndUsersOverviewTab } from 'src/permissions';

enum Type {
  PRE_PROV_PLAN = 'PRE_PROV_PLAN',
  APPLIED_PLAN = 'APPLIED_PLAN',
}

export interface GeneralDataCapacityInfo extends CapacityInfo {
  percent: number;
  consumedData?: number | string;
}

export interface DataResourceCapacity extends ResourceCapacity {
  percent: number;
  display: string;
  noSupport: boolean;
}
@UntilDestroy()
@Component({
  selector: 'app-card-end-users-plan',
  templateUrl: './card-end-users-plan.component.html',
  styleUrls: ['./card-end-users-plan.component.less'],
})
export class CardEndUsersPlanComponent implements OnInit {
  strokeColor = ColorsConst.primary;
  strokeColorQueued = ColorsConst.warning;
  strokeColorSubscriptionPaused = ColorsConst.warning;
  strokeColorOverCapacity = ColorsConst.overcapacity;
  strokeColorUnlimited = { '0%': '#1890ff', '100%': '#002766' };
  PLANS_PREFIX = 'PAGE.MOBILE.END_USERS.OVERVIEW.TELECOM_SERVICES.';
  GROUPS_MECTRIC_USAGE_PREFIX = 'PAGE.MOBILE.GROUPS.GROUP_METRICS.USAGE.';
  CREATE_PLAN_MODAL_PREFIX = 'PAGE.MOBILE.PLANS.CREATE_PLAN_MODAL.';
  DATABACK_PREFIX = 'PAGE.DATA_REWARDS.OFFERS_DATABACK_MANAGEMENT.';
  Type = Type;
  isDeletingPreProvPlan = false;
  confirmModal?: NzModalRef;
  nzOkText: string;
  unlimitedText: string;
  nzCancelText: string;
  UserPlanStatus = UserPlanStatus;
  formatDate = DefaultConst.CARD_FORMAT_DATE;
  resList = MobileEndUsersOverviewTab.resList;
  SubscriptionStatus = SubscriptionStatus;

  @Input() selectedLine: Line;
  @Input() activeSubscription: PlanSubscription;
  brandRolesAccess = DefaultConst.COPPEL_BRAND_ROLES_ACCESS;

  @Output() onViewSubscription: EventEmitter<void> = new EventEmitter<void>();
  @Output() onViewPlanDetails: EventEmitter<string> = new EventEmitter<string>();

  private _generalData: GeneralDataCapacityInfo;
  private _dedicatedData: GeneralDataCapacityInfo[];
  private _lowSpeedData: GeneralDataCapacityInfo[];
  private _userPlan: UserPlanV2;
  private _sms: DataResourceCapacity;
  private _voice: DataResourceCapacity;

  @Input() set userPlan(val: UserPlanV2) {
    this._userPlan = val;
    if (val) {
      this._generalData = this.calculateGeneralDataCapacity(val.dataBalances);
      this._dedicatedData = this.calculateDedicatedDataCapacity(val.dataBalances);
      this._lowSpeedData = this.calculateLowSpeedDataCapacity(val.dataBalances);
      this._sms = this.setVoiceAndDataRC(val.sms);
      this._voice = this.setVoiceAndDataRC(val.voice, 'voice');
    }
  }

  get generalData() {
    return this._generalData;
  }

  get dedicatedData() {
    return this._dedicatedData;
  }

  get lowSpeedData() {
    return this._lowSpeedData;
  }

  get userPlan() {
    return this._userPlan;
  }

  get sms() {
    return this._sms;
  }

  get voice() {
    return this._voice;
  }

  @Input() line: Line;
  @Output('deletePreProvPlan')
  outputDeletePreProvPlan: EventEmitter<null> = new EventEmitter();

  constructor(
    private modal: NzModalService,
    private eligibilityService: EligibilityService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private utilsPipe: UtilsPipe
  ) {}

  ngOnInit(): void {
    this.translateService
      .stream('MESSAGE.CONFIRM')
      .pipe(untilDestroyed(this))
      .subscribe((nzOkText) => (this.nzOkText = nzOkText));

    this.translateService
      .stream('MESSAGE.CANCEL')
      .pipe(untilDestroyed(this))
      .subscribe((nzCancelText) => (this.nzCancelText = nzCancelText));

    this.translateService
      .stream(this.getStrPath('UNLIMITED'))
      .pipe(untilDestroyed(this))
      .subscribe((unlimitedText) => (this.unlimitedText = unlimitedText));
  }

  /**
   * Calculate capacity
   * @param remaining
   * @param initial
   */
  calculateCapacity(remaining: number, initial: number) {
    return (remaining / initial) * 100;
  }

  setVoiceAndDataRC(
    resourceCapacity: ResourceCapacity,
    type?: string
  ): DataResourceCapacity {
    if (resourceCapacity.unlimited) {
      if (!hasIn(resourceCapacity, 'remaining')) {
        return {
          ...resourceCapacity,
          remaining: resourceCapacity.initial - resourceCapacity.consumed,
          percent: this.calculateCapacity((resourceCapacity.initial -
              (resourceCapacity.initial - resourceCapacity.consumed)),
            resourceCapacity.initial),
          display: this.getStrPath('UNLIMITED'),
          noSupport: false
        };
      } else {
        return {
          ...resourceCapacity,
          percent: this.calculateCapacity((resourceCapacity.initial - resourceCapacity.remaining),
            resourceCapacity.initial),
          display: this.getStrPath('UNLIMITED'),
          noSupport: false
        };
      }
    } else if (
      hasIn(resourceCapacity, 'initial') &&
      hasIn(resourceCapacity, 'remaining')
    ) {
      let remaining = resourceCapacity.remaining;
      let initial = resourceCapacity.initial;

      if (type === 'voice') {
        remaining = Math.round(
          this.utilsPipe.transform(remaining, 'convertSecToMin')
        );
        initial = Math.round(
          this.utilsPipe.transform(initial, 'convertSecToMin')
        );
      }

      return {
        ...resourceCapacity,
        display: `${remaining}/${initial}`,
        percent: this.calculateCapacity((initial - remaining), initial),
        noSupport: false
      };
    } else if (
      hasIn(resourceCapacity, 'initial') &&
      hasIn(resourceCapacity, 'consumed')
    ) {
      let remaining = resourceCapacity.initial - resourceCapacity.consumed;
      let initial = resourceCapacity.initial;

      if (type === 'voice') {
        remaining = Math.round(
          this.utilsPipe.transform(remaining, 'convertSecToMin')
        );
        initial = Math.round(
          this.utilsPipe.transform(initial, 'convertSecToMin')
        );
      }

      return {
        ...resourceCapacity,
        remaining,
        display: `${remaining}/${initial}`,
        percent: this.calculateCapacity(resourceCapacity.consumed, initial),
        noSupport: false
      };
    } else {
      return {
        ...resourceCapacity,
        display: '0/0',
        initial: 0,
        remaining: 0,
        unlimited: false,
        percent: 0,
        noSupport: true
      };
    }
  }

  calculateDedicatedDataCapacity(dataBalances: CapacityInfo[]): GeneralDataCapacityInfo[] {
    const filteredByTraficLabel = dataBalances.filter((dataBalance: CapacityInfo) => {
      return dataBalance.trafficLabelId !== TrafficLabelId.GENERAL &&
              dataBalance.speed === TrafficSpeed.SPEED_MAX;
    });
    const dedicatedDatas = [];

    filteredByTraficLabel.forEach(dedicatedData => {
      dedicatedDatas.push({
        ...dedicatedData,
        percent: hasIn(dedicatedData, 'unlimited') && dedicatedData.unlimited
          ? 100
          : this.calculateCapacity(dedicatedData.initial - dedicatedData.remaining, dedicatedData.initial),
        consumedData: dedicatedData.initial - dedicatedData.remaining
      });
    });

    return dedicatedDatas;
  }

  calculateGeneralDataCapacity(
    dataBalances: CapacityInfo[]
  ): GeneralDataCapacityInfo {
    const generals = dataBalances.filter((dataBalance: CapacityInfo) => {
      return dataBalance.trafficLabelId === TrafficLabelId.GENERAL;
    });

    // Use SPEED_MAX if multiple trafficLabel with the same trafficLabelIds are being used
    let general = null;
    if (generals.length > 1) {
      general = generals.find((dataBalance: CapacityInfo) => {
        return dataBalance.speed === Speed.SPEED_MAX;
      });
    } else {
      general = generals[0];
    }

    if (hasIn(general, 'unlimited') && general.unlimited) {
      return {
        ...general,
        percent: 100,
      };
    }

    if (hasIn(general, 'consumed')) {
      return {
        ...general,
        percent: this.calculateCapacity(general.consumed, general.initial),
        consumedData: general.consumed
      };
    }

    return {
      ...general,
      percent: this.calculateCapacity(general.initial - general.remaining, general.initial),
      consumedData: (parseInt(general.initial) - parseInt(general.remaining))
    };
  }

  calculateLowSpeedDataCapacity(dataBalances: CapacityInfo[]): GeneralDataCapacityInfo[] {
    const filteredByLowSpeed = dataBalances.filter((dataBalance: CapacityInfo) => {
      return dataBalance.speed === TrafficSpeed.SPEED_512KBPS;
    });
    const lowSpeeds = [];

    filteredByLowSpeed.forEach(lowSpeed => {
      lowSpeeds.push({
        ...lowSpeed,
        percent: hasIn(lowSpeed, 'unlimited') && lowSpeed.unlimited
          ? 100
          : this.calculateCapacity(lowSpeed.remaining, lowSpeed.initial),
      });
    });

    return lowSpeeds;
  }

  /**
   * Build string path for i18n
   * @param path
   */
  getStrPath(path) {
    return `${this.PLANS_PREFIX}${path}`;
  }

  /**
   * Delete the pre-provisioned plan
   * @param displayName plan display name
   * @param resolve resolve fn
   * @param reject  reject fn
   */
  deletePreProvPlan(displayName: string, resolve: any, reject: any) {
    this.isDeletingPreProvPlan = true;

    const updateUserPlanParams: UpdateUserPlanParams = {
      lineId: this.selectedLine.lineId,
      userPlanId: this.userPlan.userPlanId,
      status: UserPlanStatus.USERPLAN_CANCELED,
    };

    this.eligibilityService
      .updateUserPlan(updateUserPlanParams)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.isDeletingPreProvPlan = false;
          this.notificationService.success(
            this.getStrPath('DELETE_PRE_PROV_PLAN_SUCCESS_TITLE'),
            this.getStrPath('DELETE_PRE_PROV_PLAN_SUCCESS_MSG'),
            displayName
          );
          // this.consumersService.deleteSimPreProvPlan(); // notify parent component
          this.userPlan = null;
          this.outputDeletePreProvPlan.emit(null); // output to parent component

          resolve();
        },
        () => {
          this.isDeletingPreProvPlan = false;
          reject();
        }
      );
  }

  /**
   * show the delete plan confirmation modal
   * @param displayName the plan display name
   */
  showConfirm(displayName: string): void {
    this.translateService
      .stream(this.getStrPath('DELETE_PRE_PROV_PLAN_CONFIRM_TITLE'))
      .pipe(untilDestroyed(this))
      .subscribe((nzTitle) => {
        this.confirmModal = this.modal.confirm({
          nzOkText: this.nzOkText,
          nzCancelText: this.nzCancelText,
          nzTitle,
          nzOnOk: () => {
            new Promise((resolve, reject) => {
              this.deletePreProvPlan(displayName, resolve, reject);
            });
          },
        });
      });
  }

  viewSubscription(): void {
    this.onViewSubscription.emit();
  }

  viewPlanDetails(planObject): void {
    this.onViewPlanDetails.emit(planObject);
  }
}
